import classNames from 'classnames';

import { Row } from '~components/table-view';

import { getContributionStatus } from './contributionStatus';

export type SelectionState = 'none' | 'some' | 'all';

export interface BulkUpdateRow {
  id: string;
  client_id: number;
  client_name: string;
  category_name: string;
  // The category's maximum, when it has one. Null means no limit is known.
  category_max: number | null;
  funding_source_name: string;
  contrib_proportion: number | null;
  start_date: string;
  end_date: string | null;
}

/** What the columns need to draw and drive the selection checkboxes. */
export interface ContributionSelection {
  selectedIds: ReadonlySet<string>;
  selectableIdsByClient: ReadonlyMap<number, string[]>;
  onToggleContribution: (id: string) => void;
  onToggleClient: (clientId: number) => void;
  // The client's display name, for the checkboxes' accessible labels.
  clientName: (clientId: number) => string;
}

const isSavedContribution = (row: Row) => row.row_type === 'contribution' && !String(row.id).startsWith('new-');

/**
 * Whether a row can be part of a bulk update: a saved contribution that is Current or Future as of
 * `today`. Ended rows and unsaved new rows never are.
 */
export const isSelectableContribution = (row: Row, today: string): boolean =>
  isSavedContribution(row) &&
  !!row.start_date &&
  getContributionStatus(row.start_date, row.end_date, today) !== 'ended';

/**
 * Rows in edit mode are never selectable: a bulk update would split the saved rate, then the row's
 * stale draft (old %, no end date) would undo it on Save. The preview would also show draft values.
 */
const isBulkSelectable = (row: Row, today: string, editingIds: ReadonlySet<string | number>): boolean =>
  !editingIds.has(row.id) && isSelectableContribution(row, today);

/** The selectable contribution ids of each client in the loaded rows, in list order, leaving out rows in edit mode. */
export const groupSelectableByClient = (
  rows: Row[],
  today: string,
  editingIds: ReadonlySet<string | number> = new Set(),
): Map<number, string[]> => {
  const byClient = new Map<number, string[]>();
  rows.forEach((row) => {
    if (!isBulkSelectable(row, today, editingIds)) return;
    const ids = byClient.get(row.client_id) ?? [];
    ids.push(String(row.id));
    byClient.set(row.client_id, ids);
  });
  return byClient;
};

/** How many of `ids` are selected: none, some (indeterminate) or all. An empty list is none. */
export const getSelectionState = (ids: string[], selected: ReadonlySet<string>): SelectionState => {
  const count = ids.filter((id) => selected.has(id)).length;
  if (count === 0) return 'none';
  return count === ids.length ? 'all' : 'some';
};

/** Every selectable contribution id in the loaded rows, across all clients. */
export const flattenSelectableIds = (byClient: ReadonlyMap<number, string[]>): string[] =>
  Array.from(byClient.values()).flat();

/** Whether none, some or all of the selectable loaded rows are selected (the header checkbox). */
export const getPageSelectionState = (
  byClient: ReadonlyMap<number, string[]>,
  selected: ReadonlySet<string>,
): SelectionState => getSelectionState(flattenSelectableIds(byClient), selected);

/** Ticks `ids` unless every one is already ticked, in which case it clears them. Returns a new Set. */
export const toggleIds = (selected: ReadonlySet<string>, ids: string[]): Set<string> => {
  const next = new Set(selected);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
  return next;
};

/**
 * Drops ids that are no longer selectable rows in the loaded data (after a search, a delete, a row
 * that has since ended, or a row that has gone into edit mode). Returns the same Set when nothing
 * changed so state does not re-render.
 */
export const pruneSelection = (
  selected: Set<string>,
  rows: Row[],
  today: string,
  editingIds: ReadonlySet<string | number> = new Set(),
): Set<string> => {
  if (selected.size === 0) return selected;

  const selectable = new Set<string>();
  rows.forEach((row) => {
    if (isBulkSelectable(row, today, editingIds)) selectable.add(String(row.id));
  });

  const kept = Array.from(selected).filter((id) => selectable.has(id));
  return kept.length === selected.size ? selected : new Set(kept);
};

/**
 * The checkbox's slot always takes up its space, so hovering never shifts the layout. It is only
 * drawn (see `.contribution-select` in _configs.scss) when its row is hovered, focused, ticked, or
 * anything at all is selected - which is what `is-visible` says for the last two.
 */
export const getSelectClassName = (checked: boolean, anySelected: boolean): string =>
  classNames('contribution-select', { 'is-visible': checked || anySelected });

/** The selected rows as the bulk update modal previews them, in list order, with their client's name. */
export const buildBulkUpdateRows = (rows: Row[], selected: ReadonlySet<string>): BulkUpdateRow[] => {
  const clientNames = new Map<number, string>();
  rows.forEach((row) => {
    if (row.row_type === 'client') clientNames.set(row.client_id, row.client_name);
  });

  return rows
    .filter((row) => row.row_type === 'contribution' && selected.has(String(row.id)))
    .map((row) => ({
      id: String(row.id),
      client_id: row.client_id,
      client_name: clientNames.get(row.client_id) ?? `Unknown client (#${row.client_id})`,
      category_name: row.category?.name ?? '',
      category_max: row.category?.max_contrib_proportion ?? null,
      funding_source_name: row.funding_source_name ?? '',
      contrib_proportion: row.contrib_proportion ?? null,
      start_date: row.start_date,
      end_date: row.end_date ?? null,
    }));
};

/**
 * The header checkbox's toggle, the same as a client's: unless every selectable loaded row is already
 * selected it selects them all (so an indeterminate click completes the set), and only a fully
 * checked header clears them. Only loaded rows are touched; other ids in the selection are left
 * alone. Returns a new Set.
 */
export const toggleAllOnPage = (selected: ReadonlySet<string>, byClient: ReadonlyMap<number, string[]>): Set<string> =>
  toggleIds(selected, flattenSelectableIds(byClient));
